import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestViewTeamsForProgramme,
  requestDownloadTeamExcel,
  requestUploadTeamExcel,
  requestViewTeamLeaderboard
} from './programme-teams.actions';
import {
  getViewTeamsForProgrammeRequest,
  getDownloadTeamExcelRequest,
  getUploadTeamExcelRequest,
  getViewTeamLeaderboardRequest
} from './programme-teams.endpoints';
import {
  RETRIEVING_TEAMS,
  DOWNLOADING_TEAM_EXCEL,
  UPLOADING_TEAM_EXCEL,
  RETRIEVING_TEAM_LEADERBOARD
} from './programme-teams.constants';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setTeams } from '../../slices/programme/team/team.slice';
import { setTeamLeaderboard } from '../../slices/programme/accountability/accountability.slice';

import { SUCCESS, ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { downloadExcelFile } from '../../utils/util';

export function* performViewTeamsForProgrammeRequest({ payload }) {
  const { externalReference } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TEAMS));

    const [endpoint, options] = getViewTeamsForProgrammeRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setTeams(data.teams));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_TEAMS));
  }
}

export function* watchForViewTeamsForProgrammeRequest() {
  yield takeLatest(requestViewTeamsForProgramme.type, performViewTeamsForProgrammeRequest);
}

export function* performDownloadTeamExcelRequest({ payload }) {
  const { externalReference } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_TEAM_EXCEL));

    const [endpoint, options] = getDownloadTeamExcelRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    yield call(downloadExcelFile, data.data, data.documentName);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DOWNLOADING_TEAM_EXCEL));
  }
}

export function* watchForDownloadTeamExcelRequest() {
  yield takeLatest(requestDownloadTeamExcel.type, performDownloadTeamExcelRequest);
}

export function* performUploadTeamExcelRequest({ payload }) {
  const { externalReference, base64, onError } = payload;

  try {
    yield put(addSpinner(UPLOADING_TEAM_EXCEL));

    const request = getUploadTeamExcelRequest(externalReference, base64);

    const { data } = yield call(axios, ...request);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    yield put(requestViewTeamsForProgramme({ externalReference }));
  } catch (error) {
    const results = error.response?.data?.results;

    if (results) {
      yield call(onError, results);
    } else {
      const message = getErrorMessage(error);
      yield put(addSystemNotice({ type: ERROR, message }));
    }
  } finally {
    yield put(removeSpinner(UPLOADING_TEAM_EXCEL));
  }
}

export function* watchForUploadTeamExcelRequest() {
  yield takeLatest(requestUploadTeamExcel.type, performUploadTeamExcelRequest);
}

export function* performViewTeamLeaderboardRequest({ payload }) {
  const { externalReference } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TEAM_LEADERBOARD));

    const request = getViewTeamLeaderboardRequest(externalReference);

    const { data } = yield call(axios, ...request);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    delete data.message;
    yield put(setTeamLeaderboard(data));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_TEAM_LEADERBOARD));
  }
}

export function* watchForViewTeamLeaderboardRequest() {
  yield takeLatest(requestViewTeamLeaderboard.type, performViewTeamLeaderboardRequest);
}

export default function* programmeTeamsSaga() {
  yield all([
    watchForViewTeamsForProgrammeRequest(),
    watchForDownloadTeamExcelRequest(),
    watchForUploadTeamExcelRequest(),
    watchForViewTeamLeaderboardRequest()
  ]);
}
